/**
 * PMS rating calculator — weighted rating and weightage validation
 * Requirements: 9.5, 10.1, 10.2, 10.3, 10.4, 25.1, 25.2, 25.3, 25.4
 */

import { ObjectiveRating, RatingScale, Milestone } from '../types/pms';

/**
 * Calculate weighted overall rating.
 * Formula: sum(rating × weightage) / 100
 * Requirements: 10.4, 9.5
 */
export function calculateWeightedRating(
  ratings: ObjectiveRating[],
  weightages: Map<string, number>
): number {
  let weightedSum = 0;
  for (const r of ratings) {
    // An objective with no rating contributes nothing rather than NaN. Percent-tracked
    // objectives legitimately carry no objective-level rating now that the self-review no
    // longer invents one; `undefined * w` would otherwise poison the entire sum.
    if (r.rating === undefined || r.rating === null) continue;
    const w = weightages.get(r.objectiveId) ?? 0;
    weightedSum += r.rating * w;
  }
  return weightedSum / 100;
}

/**
 * Map numeric rating to RatingScale label.
 * Requirements: 10.3
 */
export function getRatingLabel(rating: RatingScale): string {
  const labels: Record<number, string> = {
    5: 'Outstanding',
    4: 'Exceeds Expectations',
    3: 'Meets Expectations',
    2: 'Needs Improvement',
    1: 'Unsatisfactory',
  };
  return labels[rating] ?? 'Unknown';
}

/**
 * Validate that all objective weightages sum to exactly 100%.
 * Requirements: 25.1, 25.2, 25.3, 25.4
 */
export function validateWeightageSum(weightages: number[]): {
  valid: boolean;
  total: number;
  remaining: number;
} {
  const total = weightages.reduce((sum, w) => sum + w, 0);
  return { valid: total === 100, total, remaining: 100 - total };
}

/**
 * Recalculate parent objective progress as weighted average of milestone progress values.
 * Uses milestone weightage for weighted average when available, falls back to simple average.
 * Requirements: 3.4
 */
export function calculateMilestoneProgress(milestones: Milestone[]): number {
  if (milestones.length === 0) return 0;
  const totalWeightage = milestones.reduce((sum, ms) => sum + (ms.weightage || 0), 0);
  if (totalWeightage > 0) {
    const weightedSum = milestones.reduce((sum, ms) => sum + ms.progressPercentage * (ms.weightage || 0), 0);
    return weightedSum / totalWeightage;
  }
  // Fallback: simple average
  const total = milestones.reduce((sum, ms) => sum + ms.progressPercentage, 0);
  return total / milestones.length;
}
